// One-dialog competition + registration creation (ADR 0037): the Competitions
// tab's top-level "Add Tournament"/"Add League" open this instead of the old
// inline name-only row. Builds the full structure (name, contests, sport-
// autofilled editable divisions) AND — by default — the registration, in ONE
// server round-trip (POST /organizations/:id/onboarding/build with an optional
// `registration` block). "Basic" stays here with a toast; "Custom" lands on
// the sectioned registration edit page.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import useOnboardingStore from '@store/onboardingStore';
import helpers from '@/utils/helpers';
import { browserTimeZone } from '@/utils/registrationDateTz';
import {
  getOnboardingTemplates,
  ONBOARDING_SUPPORTED_SPORTS,
} from '@staty/shared/utils/onboardingTemplates';
import StructureEditor, { cleanStructureDraft, type StructureDraft } from '@components/StructureEditor';

export type CompetitionType = 'tournament' | 'league';

interface CreateCompetitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: CompetitionType;
  organization: { _id?: unknown; sports?: string[] } | null | undefined;
  onCreated?: () => void | Promise<void>;
}

interface BuildResult {
  registrationId?: unknown;
  registrationError?: string;
}

// Sport-autofilled starting draft. getOnboardingTemplates silently falls back
// to the BASKETBALL templates for unknown sports — never let "Upper Division"
// leak into a soccer org, so unsupported sports get one generic division.
function initialDraft(type: CompetitionType, sport: string): StructureDraft {
  const contestType = type === 'tournament' ? 'tournament' : 'season';
  const supported = (ONBOARDING_SUPPORTED_SPORTS as readonly string[]).includes(sport);
  const template = supported
    ? getOnboardingTemplates(sport).find((t) => t.contestType === contestType)
    : null;
  return {
    name: '',
    contestType,
    contests: [...(template?.contests || [])],
    divisions: template
      ? template.divisions.map((d) => ({ ...d }))
      : [{ name: 'Open Division', format: 'round-robin', capacity: 8 }], // capacity 8: FORM SEED, not a platform default (ADR 0035)
  };
}

export default function CreateCompetitionDialog({
  open,
  onOpenChange,
  type,
  organization,
  onCreated = undefined,
}: CreateCompetitionDialogProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const buildStructure = useOnboardingStore((s) => s.buildStructure(getToken));

  const sport = (organization?.sports?.[0] || '').toLowerCase();
  const isTournament = type === 'tournament';

  const [draft, setDraft] = useState(() => initialDraft(type, sport));
  const [includeRegistration, setIncludeRegistration] = useState(true);
  const [regType, setRegType] = useState('basic'); // 'basic' | 'custom'
  const [openNow, setOpenNow] = useState(true);
  const [creating, setCreating] = useState(false);

  // Re-seed whenever the dialog (re)opens or the type/sport changes.
  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft(type, sport));
    setIncludeRegistration(true);
    setRegType('basic');
    setOpenNow(true);
  }, [open, type, sport]);

  const onCreate = async () => {
    if (creating) return;
    const clean = cleanStructureDraft(draft);
    if (!draft.name.trim()) {
      toast.error(`Give your ${isTournament ? 'tournament' : 'league'} a name.`);
      return;
    }
    if (!clean.divisions.length) {
      toast.error('Add at least one division.');
      return;
    }
    setCreating(true);
    try {
      const result: BuildResult = await buildStructure(helpers.getObjId(organization?._id as string), {
        ...clean,
        ...(includeRegistration
          ? { registration: { open: openNow, timeZone: browserTimeZone() } }
          : {}),
      });
      await onCreated?.();
      onOpenChange(false);

      const registrationId = helpers.getObjId(result?.registrationId as string);
      if (result?.registrationError) {
        toast.warning(`${clean.name} created, but the registration could not be created. Attach one from the Registrations page.`);
      } else if (registrationId && regType === 'custom') {
        navigate(`/admin/registration/${registrationId}/edit`);
      } else if (registrationId) {
        toast.success(`${clean.name} created. Registration is ${openNow ? 'open' : 'ready (not open yet)'}.`, {
          action: {
            label: 'View registration',
            onClick: () => navigate(`/admin/registration/${registrationId}/edit`),
          },
        });
      } else {
        toast.success(`${clean.name} created with ${clean.divisions.length} division${clean.divisions.length > 1 ? 's' : ''}.`);
      }
    } catch (e) {
      toast.error((e as Error)?.message || `Could not create your ${isTournament ? 'tournament' : 'league'}.`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!creating) onOpenChange(o); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-tour="create-competition-dialog">
        <DialogHeader>
          <DialogTitle>{isTournament ? 'New Tournament' : 'New League'}</DialogTitle>
          <DialogDescription>
            Set up the {isTournament ? 'tournament and its contests and divisions' : 'league and its divisions'}, and
            open registration in the same step.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <StructureEditor value={draft} onChange={setDraft} />

          <div className="space-y-3 rounded-lg border p-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox
                checked={includeRegistration}
                onCheckedChange={(v) => setIncludeRegistration(!!v)}
                data-tour="create-competition-include-registration"
              />
              Include registration
            </label>

            {includeRegistration && (
              <div className="space-y-3 pl-6">
                <RadioGroup value={regType} onValueChange={setRegType} className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'basic', label: 'Basic Registration', hint: 'Sensible defaults (configure later)' },
                    { value: 'custom', label: 'Custom Registration', hint: 'Configure payments, dates and forms next' },
                  ].map((opt) => (
                    <Label
                      key={opt.value}
                      htmlFor={`regtype-${opt.value}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
                        regType === opt.value ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'
                      }`}
                    >
                      <RadioGroupItem id={`regtype-${opt.value}`} value={opt.value} className="mt-0.5" />
                      <span>
                        <span className="block text-sm font-semibold">{opt.label}</span>
                        <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                      </span>
                    </Label>
                  ))}
                </RadioGroup>

                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={openNow} onCheckedChange={setOpenNow} data-tour="create-competition-open-now" />
                  Open registration immediately
                </label>
                <p className="text-xs text-muted-foreground">
                  {openNow
                    ? 'Teams can sign up as soon as this is created. Free until you attach a fee.'
                    : 'Created closed. Open it from the registration page when you’re ready.'}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>Cancel</Button>
          <Button onClick={onCreate} disabled={creating} data-tour="create-competition-submit">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
